// aifab - Runs page
import { useState, useCallback, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../hooks/apiFetch';
import { Spinner, EmptyState, Hint } from '../components/common';
import { RunRow } from '../components/runs/RunRow';
import { CompareModal } from '../components/runs/CompareModal';

export default function RunsPage({ projectId, onNavigate, onCreateRun }) {
  const { data: taskStatuses } = useApi('/meta/task-statuses');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [runs,         setRuns]         = useState(null);
  const [runsLoading,  setRunsLoading]  = useState(false);
  // showCreate removed - creation is now a full page via onCreateRun
  const [compareA,     setCompareA]     = useState(null);
  const [compareB,     setCompareB]     = useState(null);
  const [showCompare,  setShowCompare]  = useState(false);
  const [page,         setPage]         = useState(1);
  const [totalRuns,    setTotalRuns]    = useState(0);
  const PAGE_SIZE = 50;

  const STATUSES = ['ALL', ...((taskStatuses||[]).map(s => s.status_code))];

  const loadRuns = useCallback(async (p = page) => {
    if (!projectId) return;
    setRunsLoading(true);
    try {
      const d = await apiFetch(`/runs?project_id=${projectId}&page=${p}&page_size=${PAGE_SIZE}`);
      // Handle paginated response
      if (d && d.items) {
        setRuns(d.items);
        setTotalRuns(d.total || 0);
      } else {
        setRuns(Array.isArray(d) ? d : []);
      }
    } catch(_) { setRuns([]); }
    finally { setRunsLoading(false); }
  }, [projectId, page]);

  const silentRefresh = useCallback(async () => {
    // Merge by run id - preserves component identity so expanded/LogViewer state survives
    try {
      const d = await apiFetch(`/runs?project_id=${projectId}&page=1&page_size=50`);
      const items = d?.items ?? (Array.isArray(d) ? d : []);
      const liveIds = new Set(items.map(r => r.id));
      setRuns(prev => {
        if (!prev) return items;
        const map = Object.fromEntries(items.map(r => [r.id, r]));
        const updated = prev
          .filter(r => liveIds.has(r.id))
          .map(r => map[r.id] ? {...r, ...map[r.id]} : r);
        const existingIds = new Set(prev.map(r => r.id));
        const newRows = items.filter(r => !existingIds.has(r.id));
        return [...updated, ...newRows];
      });
    } catch(_) {}
  }, [projectId]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  // Auto-refresh every 10s while active runs exist - silent (no spinner)
  useEffect(() => {
    const ACTIVE = new Set(['RUNNING', 'QUEUED', 'WAITING_FOR_RESOURCES']);
    const hasActive = (runs||[]).some(r => ACTIVE.has(r.status));
    if (!hasActive) return;
    const t = setTimeout(silentRefresh, 10000);
    return () => clearTimeout(t);
  }, [runs, silentRefresh]);

  const filtered = (runs||[]).filter(r => statusFilter === 'ALL' || r.status === statusFilter);

  const selectCompare = (run) => {
    if (!compareA)                          { setCompareA(run); return; }
    if (!compareB && run.id !== compareA.id) { setCompareB(run); return; }
    setCompareA(run); setCompareB(null);
  };

  if (!projectId) return (
    <div className="page">
      <EmptyState icon="▶" title="Select a project"
        subtitle="Choose a project from the sidebar to view its experiments"
        action={<button className="btn btn-primary btn-sm" onClick={() => onNavigate('projects')}>Go to Projects</button>} />
    </div>
  );

  return (
    <div className="page">
      <div className="page-hd">
        <div style={{ display:'flex', alignItems:'center' }}>
          <div className="page-sub">Each run is an experiment attempt - create, start, compare, clone</div>
          <Hint text="A run trains one model config against one dataset + feature config. Runs produce Model Versions (visible on the Models page) - a run is the attempt, a version is the resulting trained artifact." position="bottom">
            <span />
          </Hint>
        </div>
        <div className="flex gap8">
          {compareA && compareB && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowCompare(true)}>
              ⇄ Compare #{compareA.run_number} vs #{compareB.run_number}
            </button>
          )}
          <button className="btn btn-primary btn-sm" data-tour="runs-new" onClick={onCreateRun}>+ New Run</button>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex gap6 mb20" data-tour="runs-filter" style={{ flexWrap:'wrap' }}>
        {STATUSES.map(s => (
          <button key={s} className={`btn btn-sm ${statusFilter===s?'btn-primary':'btn-ghost'}`}
            onClick={() => setStatusFilter(s)}>
            {s === 'ALL' ? 'All' : s.replace(/_/g,' ')}
            {s !== 'ALL' && runs && (
              <span style={{ marginLeft:4, opacity:.6 }}>
                ({(runs||[]).filter(r=>r.status===s).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Run list */}
      {!projectId ? null : runsLoading ? (
        <div className="flex" style={{ justifyContent:'center', padding:48 }}><Spinner size={28}/></div>
      ) : filtered.length === 0 ? (
        (runs||[]).length === 0 ? (
          <EmptyState icon="▶" title="No runs yet"
            subtitle="Create a run to kick off your first training experiment"
            action={<button className="btn btn-primary btn-sm" onClick={onCreateRun}>+ New Run</button>} />
        ) : (
          <EmptyState icon="◈" title={`No ${statusFilter} runs`} subtitle="Try a different filter" />
        )
      ) : (
        <div data-tour="runs-row">
          {filtered.map(run => (
            <RunRow
              key={run.id}
              run={run}
              onReload={silentRefresh}
              onDeleted={(runId) => {
                // Optimistically remove from list immediately
                setRuns(prev => (prev||[]).filter(r => r.id !== runId));
              }}
              onCloned={(clonedRun) => {
                setRuns(prev => [clonedRun, ...(prev || [])]);
                setTimeout(silentRefresh, 1500);
              }}
              selected={run.id === compareA?.id || run.id === compareB?.id}
              onSelect={() => selectCompare(run)}
            />
          ))}
        </div>
      )}


      {showCompare && compareA && compareB && (
        <CompareModal
          runA={compareA} runB={compareB}
          onClose={() => { setShowCompare(false); setCompareA(null); setCompareB(null); }}
        />
      )}
    </div>
  );
}
